import React, { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import ReactMarkdown from "react-markdown";
import { api, fmtPct, fmtOdds, fmtSigned } from "@/lib/api";
import { PageHeader } from "@/components/PageHeader";
import { LeagueChip } from "@/components/LeagueLogo";
import { Sparkles, Play, Loader2, TrendingUp, Target, Bot, Brain, Lock } from "lucide-react";
import { ProChip, UpgradeCard } from "@/components/ProGate";
import { usePro } from "@/context/ProContext";
import { ResponsiveContainer, PieChart, Pie, Cell, Tooltip } from "recharts";
import { toast } from "sonner";

const ITER_OPTS = [5000, 10000, 25000];

export default function Simulate() {
  const [params, setParams] = useSearchParams();
  const selected = params.get("game") || "";
  const [games, setGames] = useState([]);
  const [iterations, setIterations] = useState(10000);
  const [sim, setSim] = useState(null);
  const [busy, setBusy] = useState(false);
  const [narr, setNarr] = useState(null);
  const [narrBusy, setNarrBusy] = useState(false);
  const [stakeOpen, setStakeOpen] = useState(null);
  const [narrUpgradeOpen, setNarrUpgradeOpen] = useState(false);
  const { isPro } = usePro();

  useEffect(() => {
    api.get("/games").then((r) => setGames(r.data));
  }, []);

  const game = useMemo(() => games.find((g) => g.id === selected), [games, selected]);

  const runSim = async () => {
    if (!selected) { toast.error("Pick a game first"); return; }
    setBusy(true); setSim(null); setNarr(null);
    try {
      const { data } = await api.post(`/simulate/${selected}`, null, { params: { iterations } });
      setSim(data);
      toast.success(`${iterations.toLocaleString()} iterations complete`);
    } catch (e) {
      toast.error(e?.response?.data?.detail ?? "Simulation failed");
    } finally { setBusy(false); }
  };

  const generateNarr = async () => {
    if (!sim) return;
    setNarrBusy(true);
    try {
      const { data } = await api.post("/narrative", { game_id: selected });
      setNarr(data.text);
    } catch (e) {
      toast.error(e?.response?.data?.detail ?? "AI narrative failed");
    } finally { setNarrBusy(false); }
  };

  return (
    <div className="mx-auto max-w-7xl space-y-6" data-testid="simulate-page">
      <PageHeader
        title="Monte Carlo simulator"
        subtitle="Pick a matchup, run thousands of iterations, and uncover expected-value edges the market missed."
      />

      {/* Control panel */}
      <div className="iso-card p-5">
        <div className="grid gap-4 md:grid-cols-[1fr,220px,auto]">
          <div>
            <label className="iso-label">Game</label>
            <select
              value={selected}
              onChange={(e) => setParams(e.target.value ? { game: e.target.value } : {})}
              className="iso-input !py-2.5"
              data-testid="sim-game-select"
            >
              <option value="">Select a game…</option>
              {games.map((g) => (
                <option key={g.id} value={g.id}>
                  {g.league} · {g.away_team_name} @ {g.home_team_name}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="iso-label">Iterations</label>
            <div className="flex gap-1 rounded-lg border border-border bg-gray-50 p-1">
              {ITER_OPTS.map((n) => (
                <button
                  key={n}
                  onClick={() => setIterations(n)}
                  data-testid={`iter-${n}`}
                  className={`flex-1 rounded-md px-2 py-1.5 text-xs font-semibold transition-all ${
                    iterations === n ? "bg-primary text-primary-foreground" : "text-gray-500 hover:text-gray-900"
                  }`}
                >
                  {(n / 1000).toFixed(0)}k
                </button>
              ))}
            </div>
          </div>
          <div className="flex items-end">
            <button
              onClick={runSim}
              disabled={!selected || busy}
              className="iso-btn-primary w-full md:w-auto"
              data-testid="run-sim-btn"
            >
              {busy ? <Loader2 className="size-4 animate-spin" /> : <Play className="size-4" />}
              Run simulation
            </button>
          </div>
        </div>
      </div>

      {!sim && !busy && (
        <div className="iso-card grid-pattern p-10 text-center">
          <div className="mx-auto flex size-12 items-center justify-center rounded-xl bg-primary text-primary-foreground">
            <Sparkles className="size-6" />
          </div>
          <h3 className="mt-4 font-lexend">Ready when you are.</h3>
          <p className="mt-1 text-sm text-gray-500">
            Select a game above and press <span className="font-semibold text-gray-900">Run simulation</span> to
            compute win probability, projected score, spread cover, and EV edges.
          </p>
        </div>
      )}

      {busy && (
        <div className="iso-card p-12 text-center">
          <Loader2 className="mx-auto size-8 animate-spin text-secondary" />
          <div className="mt-4 text-sm text-gray-500">Running {iterations.toLocaleString()} iterations…</div>
        </div>
      )}

      {sim && game && (
        <div className="space-y-6" data-testid="sim-results">
          {/* Headline */}
          <div className="iso-card p-6">
            <div className="flex flex-wrap items-start justify-between gap-6">
              <div>
                <div className="iso-label">Matchup</div>
                <div className="mt-1 font-lexend text-2xl font-bold text-gray-900">
                  {game.away_team_name} @ {game.home_team_name}
                </div>
                <div className="mt-1 flex items-center gap-2 text-xs text-gray-500">
                  <LeagueChip code={game.league} />
                  <span>{new Date(game.start_time).toLocaleString([], { dateStyle: "medium", timeStyle: "short" })}</span>
                  <span>· {game.venue}</span>
                </div>
              </div>
              <div className="flex items-center gap-4">
                <div className="text-center">
                  <div className="iso-label !mb-0">Projected</div>
                  <div className="mt-1 font-lexend text-3xl font-bold text-gray-900" data-testid="sim-projected-score">
                    {sim.projected_home_score.toFixed(1)} <span className="text-gray-400">–</span> {sim.projected_away_score.toFixed(1)}
                  </div>
                  <div className="mt-1 text-xs text-gray-500">
                    Spread {fmtSigned(sim.projected_spread)} · Total {sim.projected_total.toFixed(1)}
                  </div>
                </div>
                <span className={`iso-chip text-sm ${sim.confidence === "HIGH" ? "iso-chip-green" : sim.confidence === "MEDIUM" ? "iso-chip-blue" : "iso-chip-orange"}`}>
                  {sim.confidence} confidence
                </span>
              </div>
            </div>
          </div>

          {/* Core metrics */}
          <div className="grid grid-cols-1 gap-4 lg:grid-cols-3">
            {/* Win prob pie */}
            <div className="iso-card p-5 lg:col-span-1">
              <div className="flex items-center justify-between">
                <h4 className="font-lexend">Win probability</h4>
                <span className="iso-chip-secondary iso-chip">{sim.iterations.toLocaleString()}x</span>
              </div>
              <div className="h-52">
                <ResponsiveContainer width="100%" height="100%" minHeight={180}>
                  <PieChart>
                    <Pie
                      data={[
                        { name: game.home_team_abbr, value: sim.home_win_prob },
                        { name: game.away_team_abbr, value: sim.away_win_prob },
                      ]}
                      innerRadius={55} outerRadius={80} paddingAngle={2} dataKey="value"
                    >
                      <Cell fill="rgb(var(--secondary-default))" />
                      <Cell fill="rgb(var(--gray-300))" />
                    </Pie>
                    <Tooltip formatter={(v) => fmtPct(v, 1)} contentStyle={{ border: "1px solid rgb(var(--border))", borderRadius: 8, fontSize: 12 }} />
                  </PieChart>
                </ResponsiveContainer>
              </div>
              <div className="flex justify-between gap-4 text-center">
                <div className="flex-1 rounded-lg bg-secondary-lighter/60 p-3">
                  <div className="text-[10px] font-semibold uppercase tracking-wider text-secondary-dark">{game.home_team_abbr}</div>
                  <div className="font-lexend text-xl font-bold text-gray-900">{fmtPct(sim.home_win_prob)}</div>
                </div>
                <div className="flex-1 rounded-lg bg-gray-100 p-3">
                  <div className="text-[10px] font-semibold uppercase tracking-wider text-gray-500">{game.away_team_abbr}</div>
                  <div className="font-lexend text-xl font-bold text-gray-900">{fmtPct(sim.away_win_prob)}</div>
                </div>
              </div>
            </div>

            {/* Edge table */}
            <div className="iso-card p-5 lg:col-span-2">
              <div className="flex items-center justify-between">
                <h4 className="font-lexend">EV edges by market</h4>
                <span className="iso-chip iso-chip-green"><TrendingUp className="size-3" /> Top: {sim.top_edge.market} {sim.top_edge.selection}</span>
              </div>
              <div className="mt-4 space-y-2">
                <EdgeRow name={`Moneyline · ${game.home_team_abbr}`} prob={sim.home_win_prob}
                  odds={game.odds.moneyline_home} ev={sim.moneyline_home_ev} kelly={sim.kelly_home}
                  onBet={() => setStakeOpen({ market: "moneyline", selection: "home", odds: game.odds.moneyline_home, line: null })} />
                <EdgeRow name={`Moneyline · ${game.away_team_abbr}`} prob={sim.away_win_prob}
                  odds={game.odds.moneyline_away} ev={sim.moneyline_away_ev} kelly={sim.kelly_away}
                  onBet={() => setStakeOpen({ market: "moneyline", selection: "away", odds: game.odds.moneyline_away, line: null })} />
                <EdgeRow name={`Spread · ${game.home_team_abbr} ${fmtSigned(game.odds.spread)}`} prob={sim.spread_cover_prob_home}
                  odds={game.odds.spread_price_home} ev={sim.spread_home_ev}
                  onBet={() => setStakeOpen({ market: "spread", selection: "home", odds: game.odds.spread_price_home, line: game.odds.spread })} />
                <EdgeRow name={`Total · Over ${game.odds.total}`} prob={sim.total_over_prob}
                  odds={game.odds.total_over_price} ev={sim.total_over_ev}
                  onBet={() => setStakeOpen({ market: "total", selection: "over", odds: game.odds.total_over_price, line: game.odds.total })} />
              </div>
            </div>
          </div>

          {/* Factor breakdown — 14-factor self-learning engine */}
          {sim.factor_breakdown && <FactorBreakdown sim={sim} />}

          {/* AI narrative */}
          <div className="iso-card p-6">
            <div className="flex flex-wrap items-center justify-between gap-3">
              <div className="flex items-center gap-3">
                <div className="flex size-10 items-center justify-center rounded-xl bg-gray-900 text-white">
                  <Bot className="size-5" />
                </div>
                <div>
                  <h4 className="font-lexend flex items-center gap-2">
                    AI edge analyst
                    {!isPro && <ProChip />}
                  </h4>
                  <p className="text-xs text-gray-500">Powered by Claude Sonnet 4.5 — grounded in your simulation data.</p>
                </div>
              </div>
              {isPro ? (
                <button onClick={generateNarr} disabled={narrBusy} className="iso-btn-secondary" data-testid="generate-narrative-btn">
                  {narrBusy ? <Loader2 className="size-4 animate-spin" /> : <Sparkles className="size-4" />}
                  {narr ? "Regenerate" : "Generate analysis"}
                </button>
              ) : null}
            </div>
            {!isPro && (
              <div className="mt-5" data-testid="narrative-pro-gate">
                <div className="rounded-xl border border-dashed border-border bg-gray-50 p-4 flex flex-wrap items-center gap-4">
                  <div className="flex size-10 items-center justify-center rounded-xl bg-secondary/10 text-secondary">
                    <Lock className="size-5" />
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="text-sm font-semibold text-gray-900">Unlock AI narrative analysis</div>
                    <div className="text-xs text-gray-500">Get Claude Sonnet 4.5 to turn these numbers into a betting thesis. Pro feature — $9 / 30-day pass.</div>
                  </div>
                  <button
                    onClick={() => setNarrUpgradeOpen((o) => !o)}
                    className="iso-btn-primary !py-1.5 !text-xs"
                    data-testid="narrative-upgrade-btn"
                  >
                    <Sparkles className="size-3.5" /> {narrUpgradeOpen ? "Close" : "Upgrade"}
                  </button>
                </div>
                {narrUpgradeOpen && (
                  <div className="mt-4 flex justify-center">
                    <UpgradeCard feature="AI Narratives" compact onSuccess={() => setNarrUpgradeOpen(false)} />
                  </div>
                )}
              </div>
            )}
            {isPro && narrBusy && !narr && (
              <div className="mt-6 space-y-2">
                <div className="h-3 w-4/5 animate-pulse rounded bg-gray-200" />
                <div className="h-3 w-3/5 animate-pulse rounded bg-gray-200" />
                <div className="h-3 w-5/6 animate-pulse rounded bg-gray-200" />
              </div>
            )}
            {isPro && narr && (
              <div
                className="prose prose-sm mt-5 max-w-none text-gray-700 [&_strong]:text-gray-900 [&_h3]:font-lexend [&_li]:my-0.5"
                data-testid="narrative-text"
              >
                <ReactMarkdown>{narr}</ReactMarkdown>
              </div>
            )}
          </div>
        </div>
      )}

      {stakeOpen && (
        <StakeModal
          game={game}
          pick={stakeOpen}
          onClose={() => setStakeOpen(null)}
        />
      )}
    </div>
  );
}

const EdgeRow = ({ name, prob, odds, ev, kelly, onBet }) => {
  const positive = ev > 0;
  return (
    <div className="flex items-center gap-3 rounded-lg border border-border bg-gray-50/50 px-4 py-3" data-testid={`edge-row-${name}`}>
      <div className="min-w-0 flex-1">
        <div className="text-sm font-semibold text-gray-900">{name}</div>
        <div className="mt-0.5 flex items-center gap-2 text-xs text-gray-500">
          <span>Prob {fmtPct(prob)}</span>
          <span>·</span>
          <span>Odds {fmtOdds(odds)}</span>
          {typeof kelly === "number" && kelly > 0 && (
            <>
              <span>·</span>
              <span className="text-secondary-dark font-semibold">Kelly {(kelly * 100).toFixed(1)}%</span>
            </>
          )}
        </div>
      </div>
      <div className={`font-lexend text-base font-bold ${positive ? "text-green-dark" : "text-gray-400"}`}>
        {positive ? "+" : ""}{ev.toFixed(2)}%
      </div>
      <button className="iso-btn-outline !py-1.5 !text-xs" onClick={onBet} data-testid="edge-track-btn">
        <Target className="size-3.5" /> Track
      </button>
    </div>
  );
};

const StakeModal = ({ game, pick, onClose }) => {
  const [stake, setStake] = useState(50);
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    setBusy(true);
    try {
      await api.post("/bets", { game_id: game.id, ...pick, stake });
      toast.success("Bet tracked");
      onClose();
    } catch (e) {
      toast.error(e?.response?.data?.detail ?? "Failed to save bet");
    } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" data-testid="stake-modal">
      <div className="w-full max-w-md iso-card p-6">
        <h3 className="font-lexend">Track this bet</h3>
        <p className="mt-1 text-xs text-gray-500">
          {game.away_team_abbr} @ {game.home_team_abbr} · {pick.market} · {pick.selection}
          {pick.line ? ` (${pick.line > 0 ? "+" : ""}${pick.line})` : ""} at {fmtOdds(pick.odds)}
        </p>
        <div className="mt-4">
          <label className="iso-label">Stake ($)</label>
          <input type="number" min="1" value={stake} onChange={(e) => setStake(Number(e.target.value))} className="iso-input" data-testid="stake-input" />
        </div>
        <div className="mt-5 flex items-center justify-end gap-2">
          <button onClick={onClose} className="iso-btn-ghost" data-testid="stake-cancel">Cancel</button>
          <button onClick={submit} disabled={busy || !stake} className="iso-btn-primary" data-testid="stake-submit">
            {busy && <Loader2 className="size-4 animate-spin" />}
            Save bet
          </button>
        </div>
      </div>
    </div>
  );
};


const FactorBreakdown = ({ sim }) => {
  const rows = (sim.factor_breakdown || [])
    .filter((r) => Math.abs(r.contribution) > 1e-6)
    .sort((a, b) => Math.abs(b.contribution) - Math.abs(a.contribution));
  const totalMag = rows.reduce((s, r) => s + Math.abs(r.contribution), 0) || 1;

  return (
    <div className="iso-card p-6" data-testid="factor-breakdown-panel">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <div className="flex size-10 items-center justify-center rounded-xl bg-secondary/10 text-secondary">
            <Brain className="size-5" />
          </div>
          <div>
            <h4 className="font-lexend">14-factor breakdown</h4>
            <p className="text-xs text-gray-500">
              Self-learning model, trained on {sim.training_count ?? 0} settled outcome{sim.training_count === 1 ? "" : "s"}.
            </p>
          </div>
        </div>
        <div className="flex items-center gap-4 text-right">
          <div>
            <div className="iso-label !mb-0">Base expected margin</div>
            <div className="font-lexend text-sm font-bold text-gray-900">{fmtSigned((sim.expected_margin ?? 0) - (sim.margin_bias ?? 0), 2)}</div>
          </div>
          <div>
            <div className="iso-label !mb-0">Learned bias</div>
            <div className={`font-lexend text-sm font-bold ${sim.margin_bias > 0 ? "text-green-dark" : sim.margin_bias < 0 ? "text-red-dark" : "text-gray-500"}`}>
              {fmtSigned(sim.margin_bias ?? 0, 2)}
            </div>
          </div>
        </div>
      </div>

      {rows.length === 0 ? (
        <div className="mt-6 rounded-lg border border-dashed border-border bg-gray-50 p-6 text-center text-xs text-gray-500">
          Model hasn't trained yet — all 14 weights are zero, so the base ELO model drives this pick.
          The engine starts learning as tracked bets settle.
        </div>
      ) : (
        <div className="mt-5 space-y-1.5">
          {rows.map((r) => {
            const pctWidth = Math.min(100, (Math.abs(r.contribution) / totalMag) * 100);
            const pos = r.contribution > 0;
            return (
              <div
                key={r.name}
                className="flex items-center gap-3 rounded-md border border-border px-3 py-2 text-xs"
                data-testid={`sim-factor-${r.name}`}
              >
                <div className="min-w-0 flex-1">
                  <div className="font-semibold text-gray-900">{r.label}</div>
                  <div className="text-[10px] uppercase tracking-wider text-gray-400">
                    value {fmtSigned(r.value, 2)} · weight {fmtSigned(r.weight, 3)}
                  </div>
                </div>
                <div className="hidden md:block w-40">
                  <div className="h-1.5 rounded-full bg-gray-100">
                    <div
                      className={`h-full rounded-full ${pos ? "bg-green" : "bg-red"}`}
                      style={{ width: `${pctWidth}%` }}
                    />
                  </div>
                </div>
                <div className={`font-lexend text-sm font-bold tabular-nums w-16 text-right ${pos ? "text-green-dark" : "text-red-dark"}`}>
                  {fmtSigned(r.contribution, 2)}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};
